import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { MoreHorizontal, Plus } from "lucide-react";
import {
  api,
  countButtons,
  markupFromPage,
  plainOf,
  pageFromOwner,
  type TreePage,
  type Broadcast,
  type BroadcastDetail,
  type Page,
  type Player,
  type Source,
  type TargetKind,
} from "@/lib/api";
import { useCan } from "@/lib/shell-context";
import { cn, revealSoon } from "@/lib/utils";
import { MessageEditor, treeProblem } from "@/components/message-editor";
import { useConfirm } from "@/components/confirm";
import { Button as UiButton } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Mono,
  Panel,
  PlayerNo,
  Select,
  Status,
  Table,
  Td,
  Th,
} from "@/components/primitives";

/**
 * Broadcasts and scheduled sends (spec §11, tickets 29–30).
 *
 * Sending runs on the server from a frozen recipient list, so closing this
 * page never stops a campaign. Blocked, deleted and banned players are left
 * out automatically. Nothing is retried: a failure is counted, and the
 * success rate is the number to watch. Broadcasts never appear in Topics; a
 * player's reply does, marked 📢 only when they reply to the broadcast itself.
 */

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface Draft {
  id: number | null;
  title: string;
  /** The message: text, optional image or video, and its button tree. */
  page: TreePage;
  targetKind: TargetKind;
  sourceId: number | null;
  player: Pick<Player, "id" | "playerNo" | "firstName" | "lastName"> | null;
  /** "draft" saves without sending — all a role without send may do. */
  when: "now" | "later" | "draft";
  /** `datetime-local` value, in the admin's own timezone. */
  at: string;
}

const EMPTY: Draft = {
  id: null,
  title: "",
  page: { text: "", buttons: [] },
  targetKind: "all",
  sourceId: null,
  player: null,
  when: "now",
  at: "",
};

/** A Date as the value a `datetime-local` input expects, in local time. */
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function problemWith(d: Draft): string | null {
  if (!d.title.trim())
    return "Give the broadcast a title; it is how a reply is marked in the Topic.";
  const tree = treeProblem(d.page);
  if (tree) return tree;
  if ((d.targetKind === "first_source" || d.targetKind === "latest_source") && d.sourceId === null)
    return "Choose the source.";
  if (d.targetKind === "player" && !d.player) return "Find the player by their #number.";
  if (d.when === "later") {
    if (!d.at) return "Choose when to send it.";
    if (new Date(d.at).getTime() < Date.now() + 30_000) return "Choose a time in the future.";
  }
  return null;
}

/** The Status pill tone for each campaign state. */
const STATUS_PILL: Record<Broadcast["status"], string> = {
  draft: "draft",
  scheduled: "scheduled",
  running: "active",
  paused: "warn",
  done: "sent",
  cancelled: "cancelled",
};

const STATUS_LABEL: Record<Broadcast["status"], string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  running: "Sending",
  paused: "Paused",
  done: "Sent",
  cancelled: "Cancelled",
};

export function BroadcastsPage() {
  const { botId } = useParams();
  // Drafting and sending are separate permissions (decision 42).
  const canEdit = useCan("broadcasts", "draft");
  const canSend = useCan("broadcasts", "send");
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);

  const sources = useQuery({
    queryKey: ["sources", botId],
    queryFn: () => api.get<Source[]>(`/bots/${botId}/sources`),
  });

  const list = useQuery({
    queryKey: ["broadcasts", botId],
    queryFn: () => api.get<Broadcast[]>(`/bots/${botId}/broadcasts`),
    // Progress only moves while something is sending.
    refetchInterval: (q) => (q.state.data?.some((b) => b.status === "running") ? 3_000 : 30_000),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["broadcasts", botId] });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "start" | "pause" | "resume" | "cancel" }) =>
      api.post(`/broadcasts/${id}/${action}`),
    onSettled: invalidate,
  });

  const audience = (b: Broadcast) => {
    const source = sources.data?.find((s) => s.id === b.targetId)?.name ?? "a source";
    if (b.targetKind === "all") return "All players";
    if (b.targetKind === "first_source") return `First came from ${source}`;
    if (b.targetKind === "latest_source") return `Latest came from ${source}`;
    return "One player";
  };

  const edit = (b: Broadcast) => {
    revealSoon("broadcast-form");
    setDraft({
      id: b.id,
      title: b.title,
      page: pageFromOwner(b.text, b.replyMarkup),
      targetKind: b.targetKind,
      sourceId: b.targetKind.endsWith("source") ? b.targetId : null,
      // The #number is not on the campaign row; the operator re-finds the player.
      player: null,
      when: b.scheduledFor ? "later" : "now",
      at: b.scheduledFor ? toLocalInput(new Date(b.scheduledFor)) : "",
    });
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {draft && (
        <Composer
          key={draft.id ?? "new"}
          botId={botId}
          initial={draft}
          sources={sources.data ?? []}
          canSend={canSend}
          onDone={() => {
            setDraft(null);
            invalidate();
          }}
          onCancel={() => setDraft(null)}
        />
      )}

      <Panel
        title="Campaigns"
        action={
          canEdit &&
          !draft && (
            <Button size="sm" onClick={() => setDraft(EMPTY)}>
              <Plus className="size-4" /> New broadcast
            </Button>
          )
        }
      >
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <div className="p-3.5">
            <ErrorNote error={list.error} />
          </div>
        ) : list.data && list.data.length === 0 ? (
          <Empty
            title="No broadcasts yet"
            hint="Send a message to every player, to one source's players, or to a single player — now or at a set time."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Campaign</Th>
                <Th className="w-32">Status</Th>
                <Th className="w-56">Delivery</Th>
                <Th className="w-44 md:max-xl:hidden">When</Th>
                <Th className="w-36" />
              </tr>
            </thead>
            <tbody>
              {list.data?.map((b) => (
                <CampaignRow
                  key={b.id}
                  broadcast={b}
                  audience={audience(b)}
                  extras={(() => {
                    const page = pageFromOwner(b.text, b.replyMarkup);
                    const n = countButtons(page);
                    return [
                      page.media ? (page.media.kind === "video" ? "video" : "image") : null,
                      n > 0 ? `${n} ${n === 1 ? "button" : "buttons"}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                  })()}
                  canEdit={canEdit}
                  canSend={canSend}
                  busy={act.isPending}
                  onAction={async (action) => {
                    if (
                      action === "cancel" &&
                      !(await confirm({
                        title: `Cancel “${b.title}”?`,
                        description: "Messages already sent stay sent.",
                        confirmLabel: "Cancel campaign",
                        destructive: true,
                      }))
                    )
                      return;
                    if (
                      action === "start" &&
                      !(await confirm({
                        title: `Send “${b.title}” now?`,
                        description: "It goes out to everyone in its audience straight away.",
                        confirmLabel: "Send now",
                      }))
                    )
                      return;
                    act.mutate({ id: b.id, action });
                  }}
                  onEdit={() => edit(b)}
                />
              ))}
            </tbody>
          </Table>
        )}
        {act.error && (
          <div className="p-3.5">
            <ErrorNote error={act.error} />
          </div>
        )}
        <p className="border-t border-border px-5 py-2.5 text-muted-foreground">
          Blocked, deleted and banned players are skipped. Sending continues if you close this page.
          Failures are not retried.
        </p>
      </Panel>
    </div>
  );
}

function CampaignRow({
  broadcast: b,
  audience,
  extras,
  canEdit,
  canSend,
  busy,
  onAction,
  onEdit,
}: {
  broadcast: Broadcast;
  audience: string;
  extras: string;
  canEdit: boolean;
  canSend: boolean;
  busy: boolean;
  onAction: (action: "start" | "pause" | "resume" | "cancel") => void | Promise<void>;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const done = b.sent + b.failed + b.blocked;
  // Editing a scheduled campaign could move its time, which is sending.
  const canChange = (canEdit && b.status === "draft") || (canSend && b.status === "scheduled");
  const canCancel = canSend && b.status !== "done" && b.status !== "cancelled";
  const hasFailures = b.failed > 0 || b.blocked > 0;
  const pct = (n: number) => (b.recipients > 0 ? `${(n / b.recipients) * 100}%` : "0%");
  const at = (iso: string) => {
    const date = new Date(iso);
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      ...(sameYear ? {} : { year: "numeric" }),
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const when =
    b.status === "scheduled" && b.scheduledFor
      ? { label: "Sends", at: b.scheduledFor }
      : b.finishedAt
        ? { label: "Finished", at: b.finishedAt }
        : b.startedAt
          ? { label: "Started", at: b.startedAt }
          : { label: "Created", at: b.createdAt };

  // Masked or hidden by the server for someone without data access (decision 42).
  const people = [b.createdBy && `Created by ${b.createdBy}`, b.sentBy && `Sent by ${b.sentBy}`]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <tr>
        <Td primary>
          <div className="grid min-w-0 gap-0.5">
            <span className="font-medium [overflow-wrap:anywhere]">{b.title}</span>
            <span className="text-muted-foreground">
              {audience}
              {extras && <> · {extras}</>}
            </span>
            {b.text && (
              <span className="line-clamp-1 text-muted-foreground [overflow-wrap:anywhere]">
                {plainOf(b.text, b.replyMarkup)}
              </span>
            )}
            {people && (
              <span className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                {people}
              </span>
            )}
          </div>
        </Td>
        <Td label="Status">
          <Status value={STATUS_PILL[b.status]} label={STATUS_LABEL[b.status]} />
        </Td>
        <Td label="Delivery">
          {b.recipients > 0 ? (
            <div className="grid w-full min-w-40 gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span>
                  <Mono className="font-medium">{b.sent.toLocaleString()}</Mono>
                  <span className="text-muted-foreground">
                    {" "}
                    / {b.recipients.toLocaleString()} sent
                  </span>
                </span>
                <span className="font-medium">
                  {b.status === "running"
                    ? `${Math.floor((done / b.recipients) * 100)}%`
                    : b.successRate !== null
                      ? `${b.successRate}%`
                      : ""}
                </span>
              </div>
              <div
                className="flex h-1.5 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuenow={done}
                aria-valuemax={b.recipients}
                aria-label="Progress"
              >
                <div className="bg-ok" style={{ width: pct(b.sent) }} />
                <div className="bg-warn" style={{ width: pct(b.blocked) }} />
                <div className="bg-destructive" style={{ width: pct(b.failed) }} />
              </div>
              {(b.blocked > 0 || b.failed > 0) && (
                <span className="text-xs text-muted-foreground">
                  {b.blocked > 0 && `${b.blocked} blocked`}
                  {b.blocked > 0 && b.failed > 0 && " · "}
                  {b.failed > 0 && `${b.failed} failed`}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </Td>
        <Td label={when.label} className="text-muted-foreground md:max-xl:hidden">
          <span className="grid">
            <span className="hidden text-xs md:inline">{when.label}</span>
            <span className="text-foreground">{at(when.at)}</span>
          </span>
        </Td>
        <Td>
          <span className="flex items-center justify-end gap-1.5">
            {canSend && (b.status === "draft" || b.status === "scheduled") && (
              <Button size="sm" disabled={busy} onClick={() => onAction("start")}>
                Send now
              </Button>
            )}
            {canSend && b.status === "running" && (
              <Button size="sm" variant="quiet" disabled={busy} onClick={() => onAction("pause")}>
                Pause
              </Button>
            )}
            {canSend && b.status === "paused" && (
              <Button size="sm" disabled={busy} onClick={() => onAction("resume")}>
                Resume
              </Button>
            )}
            {(canChange || hasFailures || canCancel) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <UiButton variant="ghost" size="icon-sm" aria-label={`More for ${b.title}`}>
                    <MoreHorizontal />
                  </UiButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canChange && <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>}
                  {hasFailures && (
                    <DropdownMenuItem onSelect={() => setOpen(!open)}>
                      {open ? "Hide failures" : "Show failures"}
                    </DropdownMenuItem>
                  )}
                  {canCancel && (
                    <DropdownMenuItem variant="destructive" onSelect={() => onAction("cancel")}>
                      Cancel campaign
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </span>
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="border-b border-border bg-muted/30 px-5 py-3">
            <Failures id={b.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function Failures({ id }: { id: number }) {
  const detail = useQuery({
    queryKey: ["broadcast", id],
    queryFn: () => api.get<BroadcastDetail>(`/broadcasts/${id}`),
  });
  if (detail.isLoading) return <Loading />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const failures = detail.data?.failures ?? [];

  return (
    <ul className="grid gap-1 rounded-md border border-border bg-muted/30 px-2.5 py-2">
      {failures.map((f) => (
        <li key={f.playerId} className="flex flex-wrap gap-x-3">
          <span className={f.status === "blocked" ? "text-amber-600" : "text-destructive"}>
            {f.status}
          </span>
          <span className="text-muted-foreground">player {f.playerId}</span>
          {f.error && <span className="min-w-0 [overflow-wrap:anywhere]">{f.error}</span>}
        </li>
      ))}
      {failures.length === 200 && <li className="text-muted-foreground">Showing the first 200.</li>}
    </ul>
  );
}

function Composer({
  botId,
  initial,
  sources,
  canSend,
  onDone,
  onCancel,
}: {
  botId: string | undefined;
  initial: Draft;
  sources: Source[];
  canSend: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [d, setD] = useState<Draft>(canSend ? initial : { ...initial, when: "draft" });
  const confirm = useConfirm();
  const [touched, setTouched] = useState(false);
  const set = (patch: Partial<Draft>) => setD({ ...d, ...patch });
  const problem = problemWith(d);

  const submit = useMutation({
    mutationFn: async () => {
      const body = {
        title: d.title.trim(),
        text: d.page.text.trim(),
        replyMarkup: markupFromPage(d.page),
        targetKind: d.targetKind,
        targetId:
          d.targetKind === "all"
            ? null
            : d.targetKind === "player"
              ? (d.player?.id ?? null)
              : d.sourceId,
        scheduledFor: d.when === "later" ? new Date(d.at).toISOString() : null,
      };
      const saved =
        d.id === null
          ? await api.post<Broadcast>(`/bots/${botId}/broadcasts`, body)
          : await api.patch<Broadcast>(`/broadcasts/${d.id}`, body);
      // If starting fails (say nobody matches), a retry edits this draft
      // rather than creating a second copy of it.
      setD((current) => ({ ...current, id: saved.id }));
      if (d.when === "now") await api.post(`/broadcasts/${saved.id}/start`);
    },
    onSuccess: onDone,
  });

  return (
    <Panel id="broadcast-form" title={d.id === null ? "New broadcast" : `Edit “${initial.title}”`}>
      <form
        className="grid gap-3 px-5 py-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setTouched(true);
          if (problem) return;
          if (
            d.when === "now" &&
            !(await confirm({
              title: "Send this broadcast now?",
              description: "It goes out to everyone in its audience straight away.",
              confirmLabel: "Send now",
            }))
          )
            return;
          submit.mutate();
        }}
      >
        <Field label="Title">
          <Input
            value={d.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Weekend deposit bonus"
            maxLength={120}
          />
        </Field>
        <p className="-mt-1 text-muted-foreground">
          Players do not see the title. CS does, when a player replies to this message.
        </p>
        <div className="grid gap-1.5">
          <div className="font-medium">Message</div>
          <div className="rounded-md border border-border p-3">
            <MessageEditor
              value={d.page}
              onChange={(page) => set({ page })}
              textPlaceholder="Double your first deposit this weekend only."
            />
          </div>
        </div>

        <Audience botId={botId} draft={d} sources={sources} set={set} />

        {!canSend && (
          <p className="rounded-md border border-border bg-muted/40 px-4 py-3 text-muted-foreground">
            Your role can write drafts. Someone who can send will review and send it.
          </p>
        )}
        {canSend && (
          <fieldset className="grid gap-2">
            <legend className="mb-1.5 font-medium">When</legend>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="when"
                  checked={d.when === "now"}
                  onChange={() => set({ when: "now" })}
                />
                Send now
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="when"
                  checked={d.when === "later"}
                  onChange={() =>
                    set({
                      when: "later",
                      at: d.at || toLocalInput(new Date(Date.now() + 3_600_000)),
                    })
                  }
                />
                Schedule
              </label>
            </div>
            {d.when === "later" && (
              <div className="grid gap-1.5 md:max-w-sm">
                <Input
                  type="datetime-local"
                  aria-label="Send at"
                  value={d.at}
                  min={toLocalInput(new Date())}
                  onChange={(e) => set({ at: e.target.value })}
                />
                <span className="text-muted-foreground">
                  Your time, {TZ}. Starts within a minute of this time.
                </span>
              </div>
            )}
          </fieldset>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={submit.isPending}>
            {submit.isPending
              ? "Working"
              : d.when === "draft"
                ? "Save draft"
                : d.when === "now"
                  ? "Send now"
                  : d.id === null
                    ? "Schedule"
                    : "Save schedule"}
          </Button>
          <Button type="button" variant="quiet" onClick={onCancel}>
            Cancel
          </Button>
          {touched && problem && <span className="text-destructive">{problem}</span>}
        </div>
        {submit.error && <ErrorNote error={submit.error} />}
      </form>
    </Panel>
  );
}

/** Finds one player by #number, name or username, and shows who it is. */
function PlayerFinder({
  botId,
  value,
  onChange,
}: {
  botId: string | undefined;
  value: Draft["player"];
  onChange: (player: Draft["player"]) => void;
}) {
  const [q, setQ] = useState("");
  const search = useQuery({
    queryKey: ["player-search", botId, q],
    queryFn: () =>
      api.get<Page<Player>>(`/bots/${botId}/players?${new URLSearchParams({ q, page: "1" })}`),
    enabled: q.trim().length > 0,
  });

  if (value) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
        <PlayerNo value={value.playerNo} />
        <span>{[value.firstName, value.lastName].filter(Boolean).join(" ") || "Player"}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => onChange(null)}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <Field label="Player">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="#01, a name or a username"
          className="md:max-w-sm"
        />
      </Field>
      {search.data && (
        <ul className="grid max-h-56 gap-px overflow-y-auto rounded-md border border-border bg-border md:max-w-sm">
          {search.data.rows.slice(0, 8).map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 bg-card px-3 py-2 text-left hover:bg-secondary"
                onClick={() => onChange(p)}
              >
                <PlayerNo value={p.playerNo} />
                <span className="min-w-0 truncate">
                  {[p.firstName, p.lastName].filter(Boolean).join(" ") || "Player"}
                </span>
              </button>
            </li>
          ))}
          {search.data.rows.length === 0 && (
            <li className="bg-card px-3 py-2 text-muted-foreground">No player matches.</li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * Who the campaign reaches, as three plain choices, with the number of
 * players it would reach right now — the same count the send will use.
 */
function Audience({
  botId,
  draft: d,
  sources,
  set,
}: {
  botId: string | undefined;
  draft: Draft;
  sources: Source[];
  set: (patch: Partial<Draft>) => void;
}) {
  const choice = d.targetKind === "all" ? "all" : d.targetKind === "player" ? "player" : "source";
  const targetId =
    d.targetKind === "player" ? (d.player?.id ?? null) : d.targetKind === "all" ? null : d.sourceId;

  const audience = useQuery({
    queryKey: ["audience", botId, d.targetKind, targetId],
    queryFn: () =>
      api.get<{ count: number }>(
        `/bots/${botId}/broadcasts/audience?${new URLSearchParams({
          targetKind: d.targetKind,
          ...(targetId ? { targetId: String(targetId) } : {}),
        })}`,
      ),
  });

  const card = (value: typeof choice, title: string, hint: string, kind: TargetKind) => (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2.5 rounded-md border p-3",
        choice === value ? "border-ring bg-accent/50" : "border-border hover:border-ring/60",
      )}
    >
      <input
        type="radio"
        name="audience"
        className="mt-1"
        checked={choice === value}
        onChange={() => set({ targetKind: kind })}
      />
      <span>
        <span className="font-medium">{title}</span>
        <span className="block text-muted-foreground">{hint}</span>
      </span>
    </label>
  );

  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1.5 font-medium">Send to</legend>
      <div className="grid gap-2 md:grid-cols-3">
        {card("all", "Everyone", "Every player of this bot.", "all")}
        {card(
          "source",
          "Players from a source",
          "Players who came through one influencer or campaign link.",
          d.targetKind === "first_source" ? "first_source" : "latest_source",
        )}
        {card("player", "One player", "Find them by #number, name or username.", "player")}
      </div>

      {choice === "source" && (
        <div className="grid gap-3 rounded-md border border-border p-3">
          <Field label="Source">
            <Select
              className="w-full md:max-w-sm"
              value={d.sourceId ?? ""}
              onChange={(e) => set({ sourceId: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">Choose a source</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <fieldset className="grid gap-1.5">
            <legend className="mb-1 font-medium">Which of their players</legend>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="touch"
                className="mt-1"
                checked={d.targetKind === "latest_source"}
                onChange={() => set({ targetKind: "latest_source" })}
              />
              <span>
                Came through it most recently
                <span className="block text-muted-foreground">
                  Their latest link was this one. Best for promoting what that source is doing now.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="touch"
                className="mt-1"
                checked={d.targetKind === "first_source"}
                onChange={() => set({ targetKind: "first_source" })}
              />
              <span>
                Joined through it originally
                <span className="block text-muted-foreground">
                  This source brought them in the first time, whatever link they used since.
                </span>
              </span>
            </label>
          </fieldset>
        </div>
      )}

      {choice === "player" && (
        <PlayerFinder botId={botId} value={d.player} onChange={(player) => set({ player })} />
      )}

      <p className="text-muted-foreground">
        {audience.data ? (
          <>
            Reaches{" "}
            <span className="font-medium text-foreground">
              {audience.data.count.toLocaleString()}{" "}
              {audience.data.count === 1 ? "player" : "players"}
            </span>{" "}
            right now. Players who blocked the bot, deleted their account or are banned are left
            out.
          </>
        ) : (
          "Counting…"
        )}
      </p>
    </fieldset>
  );
}
